"use client";

import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 132;
const PAD_X = 28;
const BOX_TOP = 26;
const BOX_BOTTOM = 72;
const AXIS_Y = 100;

export interface BoxPlotValues {
  q1: number;
  median: number;
  q3: number;
  /** Whisker ends: the most extreme values inside the fences. */
  whiskerLow: number;
  whiskerHigh: number;
  outliers: number[];
  min: number;
  max: number;
}

/**
 * A horizontal box plot drawn to scale: box from Q1 to Q3, a line at the median,
 * whiskers to the furthest values inside Tukey's fences, and outliers as points.
 *
 * Series colours come from the Okabe–Ito palette; the axis and labels use the theme's
 * text colour so the chart reads in light and dark mode alike.
 */
export function BoxPlot({
  values,
  fmt,
  label,
}: {
  values: BoxPlotValues;
  fmt: NumberFormatter;
  /** The accessible description: the five numbers in words. */
  label: string;
}) {
  const axis = niceTicks(values.min, values.max, 6);
  const span = axis.hi - axis.lo || 1;
  const x = (value: number) =>
    PAD_X + ((value - axis.lo) / span) * (WIDTH - 2 * PAD_X);
  const middle = (BOX_TOP + BOX_BOTTOM) / 2;
  const blue = CHART_COLORS[0];
  const outlierColor = CHART_COLORS[3];

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      <line
        stroke={blue}
        strokeWidth={2}
        x1={x(values.whiskerLow)}
        x2={x(values.q1)}
        y1={middle}
        y2={middle}
      />
      <line
        stroke={blue}
        strokeWidth={2}
        x1={x(values.q3)}
        x2={x(values.whiskerHigh)}
        y1={middle}
        y2={middle}
      />
      {[values.whiskerLow, values.whiskerHigh].map((end, index) => (
        <line
          // Two fixed ends; their order never changes.
          // biome-ignore lint/suspicious/noArrayIndexKey: two fixed whisker caps
          key={index}
          stroke={blue}
          strokeWidth={2}
          x1={x(end)}
          x2={x(end)}
          y1={BOX_TOP + 10}
          y2={BOX_BOTTOM - 10}
        />
      ))}
      <rect
        fill={blue}
        fillOpacity={0.15}
        height={BOX_BOTTOM - BOX_TOP}
        stroke={blue}
        strokeWidth={2}
        width={Math.max(1, x(values.q3) - x(values.q1))}
        x={x(values.q1)}
        y={BOX_TOP}
      />
      <line
        stroke={blue}
        strokeWidth={3}
        x1={x(values.median)}
        x2={x(values.median)}
        y1={BOX_TOP}
        y2={BOX_BOTTOM}
      />
      {values.outliers.map((value, index) => (
        <circle
          cx={x(value)}
          cy={middle}
          fill={outlierColor}
          // Outliers can repeat, so the value alone is not a unique key.
          // biome-ignore lint/suspicious/noArrayIndexKey: repeated outlier values
          key={index}
          r={4.5}
        />
      ))}
      <line
        stroke="currentColor"
        strokeWidth={1}
        x1={PAD_X}
        x2={WIDTH - PAD_X}
        y1={AXIS_Y}
        y2={AXIS_Y}
      />
      {axis.ticks.map((tick) => (
        <g key={tick}>
          <line
            stroke="currentColor"
            x1={x(tick)}
            x2={x(tick)}
            y1={AXIS_Y}
            y2={AXIS_Y + 5}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={x(tick)}
            y={AXIS_Y + 20}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
    </svg>
  );
}
